import React from 'react'
import { frenchMonth } from '../../../../variables/functions';
import { LOGGED_USER } from '../../../../variables/constants';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { Table, Tabs } from 'antd';
import entete from '../../../../assets/img/entete.jpg'
import axiosInstance from '../../../../variables/api/axiosInstance';

dayjs.locale('fr');

const TableauRecapitulatifRetrocession = ({ selectedGroup }) => {

    const formatNumberForExport = (value) => {
        if (!value && value !== 0) return '';
        return new Intl.NumberFormat('en-DE', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(Math.round(value)).replace(/\./g, ' ');
    };

    const loadImageAsBase64 = (url) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = reject;
            img.src = url;
        });
    };

    const fetchInsuranceLogos = async () => {
        try {
            const response = await axiosInstance.get(`/api/organisme/all/assurances`);
            return response.data || [];
        } catch (error) {
            console.error("Erreur lors de la récupération des logos:", error);
            return [];
        }
    };

    // jsPDF ne sait pas décoder toutes les variantes PNG (entrelacé « Adam7 », 16 bits, certains
    // types de couleur) → addImage lève alors que le NAVIGATEUR, lui, affiche l'image (d'où un logo
    // visible au login mais absent du PDF, ex. FIDELIA). On ré-encode chaque logo via un <canvas> en
    // PNG standard 8 bits non entrelacé, universellement décodable par jsPDF. Renvoie null si échec.
    const sanitizeLogoForPdf = (logoDisplay) => new Promise((resolve) => {
        if (!logoDisplay) { resolve(null); return; }
        try {
            const img = new Image();
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.naturalWidth || img.width;
                    canvas.height = img.naturalHeight || img.height;
                    canvas.getContext('2d').drawImage(img, 0, 0);
                    resolve(canvas.toDataURL('image/png'));
                } catch (e) { resolve(null); }
            };
            img.onerror = () => resolve(null);
            img.src = String(logoDisplay).startsWith('data:')
                ? logoDisplay : `data:image/png;base64,${logoDisplay}`;
        } catch (e) { resolve(null); }
    });

    // ============================================================
    // Source de données UNIQUE utilisée partout (tableau, Excel, PDF)
    // ============================================================
    const createDataSource = (group) => {
        const compagniesArray = group?.retrocessions
            && Object.values(group?.retrocessions)
                ?.sort((a, b) => a.compagnie.code.localeCompare(b.compagnie.code, undefined, { numeric: true }));

        const nz = (v) => Number(v) || 0;
        // Montant de cession : version CORRIGÉE (Pool, anomalie contrat sans date) si présente,
        // sinon ORIGINALE (compagnie). Un seul point de vérité pour tableau + Excel + PDF.
        const cessTotalEm = (c) => nz(c?.cession?.totalEmissionsCorrige ?? c?.cession?.totalEmissions);

        // PRIME NETTE REPORTÉE (émissions à effet d'exercice FUTUR, non réparties ce mois) :
        // base réellement rétrocédée = cédée − reportée. On la déduit dans le bloc CESSION pour
        // cohérence avec le bloc RETRO (déjà net). Rétrocédable/cédante = Σ des lignes rétrocédables
        // (group.emissionsLignes, taguées par cédante) ; reportée = cédée (snapshot) − rétrocédable.
        const hasLignes = Array.isArray(group?.emissionsLignes) && group.emissionsLignes.length > 0;
        const retrocedableParComp = (c) => (group?.emissionsLignes || [])
            .filter((l) => l?.compagnie?.code === c?.compagnie?.code)
            .reduce((s, l) => s + (Number(l?.montantOriginal) || 0), 0);
        // Aperçu (lignes live) : reportée = cédée − rétrocédable. Vue validée (pas de lignes) : valeur
        // STOCKÉE `totalEmissionsReportees` (même source que le compte de cession PDF → cohérence).
        const reporteParComp = (c) => hasLignes
            ? Math.max(0, Math.round(cessTotalEm(c) - retrocedableParComp(c)))
            : Math.max(0, Math.round(Number(c?.totalEmissionsReportees) || 0));
        const souscriptionsNettesParComp = (c) => Math.round(cessTotalEm(c)) - reporteParComp(c);

        // Indemnité de gestion = 15 % du TOTAL DES SOUSCRIPTIONS A RETROCEDER (souscriptions − prime
        // nette reportée), et non plus du montant total des souscriptions. Sans prime reportée, les deux
        // bases sont égales. Même formule que le compte de cession PDF (RetrocessionService) et que le
        // solde de cession du back (computeCorrectionInMemory).
        const cessIndemnites = (c) => Math.round(souscriptionsNettesParComp(c) * 0.15);

        // IG 15 % sur CA retro, ARRONDIE PAR RÉPARTITION AU PLUS FORT RESTE : Σ IG retro = Σ IG de cession
        // (cessIndemnites), sinon les deux séries d'arrondis diffèrent de ±1 F et le tableau de conformité
        // n'est plus « CONFORME ». Chaque IG reste à moins de 1 F de 15 % × CA retro. Hors cas d'arrondi
        // pur → arrondi simple. MIROIR EXACT de RetrocessionService.indemnitesRetroReparties (back).
        const igRetroParCode = (() => {
            const liste = compagniesArray || [];
            const base = (c) => Math.round(c.totalEmissions || 0) * 0.15;
            const cible = liste.reduce((sum, c) => sum + (c?.cession ? cessIndemnites(c) : 0), 0);
            const aDistribuer = cible - liste.reduce((sum, c) => sum + Math.floor(base(c)), 0);
            const map = {};
            if (aDistribuer < 0 || aDistribuer > liste.length) {
                liste.forEach((c) => { map[c?.compagnie?.code] = Math.round(base(c)); });
                return map;
            }
            [...liste]
                .sort((a, b) => ((base(b) - Math.floor(base(b))) - (base(a) - Math.floor(base(a))))
                    || String(a?.compagnie?.code).localeCompare(String(b?.compagnie?.code)))
                .forEach((c, i) => { map[c?.compagnie?.code] = Math.floor(base(c)) + (i < aDistribuer ? 1 : 0); });
            return map;
        })();
        const igRetro = (c) => igRetroParCode[c?.compagnie?.code] ?? Math.round((c.totalEmissions || 0) * 0.15);

        // SOLDE DE CESSION SIGNÉ (base du chèque) = crédit − débit, tronqué au franc :
        //     crédit = souscriptions à rétrocéder (hors prime reportée) + indemnités d'annulations + recours
        //     débit  = annulations + indemnité de gestion (15 %) + sinistres
        //   > 0 ⇒ EN FAVEUR DU POOL (la compagnie doit verser) ; < 0 ⇒ EN FAVEUR DE LA COMPAGNIE
        //   (le Pool est débiteur). C'est le signe attendu par la DIFF : + |solde| Pool / − |solde| compagnie.
        //   Cession corrigée par le Pool (anomalie) → soldeCorrige déjà calculé avec la MÊME formule signée.
        const cessSoldeAffiche = (c) => {
            const cess = c?.cession;
            if (!cess) return 0;
            if (cess.soldeCorrige != null) return Math.trunc(cess.soldeCorrige);
            // Crédit = TOTAL DES SOUSCRIPTIONS A RETROCEDER (net de la prime reportée), comme le compte de
            // cession PDF validé par le DOI (2026-09-25) ; l'écart avec le chèque passe dans la DIFF.
            const credit = souscriptionsNettesParComp(c) + nz(cess.indemnitesAnnulations) + nz(cess.totalRecours);
            const debit = nz(cess.totalAnnulations) + cessIndemnites(c) + nz(cess.totalSinistres);
            return Math.round(credit - debit);
        };

        // Montant du chèque : compté SEULEMENT s'il est validé par le Pool, tronqué au franc entier.
        const montantChequeValide = (c) => {
            const cess = c?.cession;
            if (!cess || !cess.dateValidationCheque || cess.montantCheque == null) return 0;
            return Math.trunc(cess.montantCheque);
        };

        // Le chèque de cession est stocké en MAGNITUDE (|solde|), son SENS n'étant pas persisté. Il suit
        // le sens du solde (Pool → compagnie si faveur compagnie) : on le SIGNE donc comme le solde affiché
        // (négatif = faveur compagnie) pour que la DIFF retombe à 0 quand le chèque solde exactement la
        // cession, dans les DEUX sens.
        const montantChequeSigne = (c) => {
            const m = montantChequeValide(c);
            return cessSoldeAffiche(c) < 0 ? -m : m;
        };

        // DIFFÉRENCE DE SOLDE DE CESSION = montant chèque (signé) − SOLDE DE CESSION SIGNÉ, où le solde =
        //   + |solde| si en faveur du POOL ; − |solde| si en faveur de la COMPAGNIE (= cessSoldeAffiche).
        //   Nulle quand le chèque solde la cession ; sinon capte l'écart avec son sens.
        const diffSolde = (c) => montantChequeSigne(c) - cessSoldeAffiche(c);

        // --- Totaux CESSION (arrondis) ---
        const totalCessionEmissions = Math.round(
            compagniesArray?.reduce((sum, c) => sum + cessTotalEm(c), 0) || 0);
        const totalCessionSinistres = Math.round(
            compagniesArray?.reduce((sum, c) => sum + nz(c.cession?.totalSinistres), 0) || 0);
        // Solde de cession AFFICHÉ (signé) = Σ des soldes signés par compagnie ⇒ la colonne CESSION
        // s'additionne EXACTEMENT (souscriptions − sinistres − indemnité 15 %).
        const soldeCessionAffiche = Math.round(
            compagniesArray?.reduce((sum, c) => sum + cessSoldeAffiche(c), 0) || 0);
        // Indemnité de gestion totale = Σ des indemnités par compagnie (la colonne TOTAL s'additionne).
        const totalCessionIndemnites = Math.round(
            compagniesArray?.reduce((sum, c) => sum + cessIndemnites(c), 0) || 0);
        const penalitesCession = Math.round(
            compagniesArray?.reduce((sum, c) => sum + (c.penalite || 0), 0) || 0);

        const totalReporte = Math.round(
            compagniesArray?.reduce((sum, c) => sum + reporteParComp(c), 0) || 0);
        const totalSouscriptionsNettes = totalCessionEmissions - totalReporte;
        // On n'affiche les lignes « reportée / nettes » QUE s'il y a effectivement une prime reportée.
        const afficherReporte = totalReporte > 1;

        // --- Totaux RETROCESSION (arrondis) ---
        const totalRetroEmissions = Math.round(
            compagniesArray?.reduce((sum, c) => sum + (c.totalEmissions || 0), 0) || 0);
        const totalRetroSinistres = Math.round(
            compagniesArray?.reduce((sum, c) => sum + (c.totalSinistres || 0), 0) || 0);
        const totalRetroIndemnitesGestion = (compagniesArray || []).reduce((sum, c) => sum + igRetro(c), 0);
        const totalRetroFraisAdmin = Math.round(totalRetroEmissions * 0.06);
        const totalRetroPreventionRoutiere = Math.round(totalRetroEmissions * 0.01);
        const totalRetroReserveGarantie = Math.round(totalRetroEmissions * 0.05);

        // --- Solde de rétrocession par compagnie ---
        // CA net de rétrocession = somme des postes RETRO affichés (émissions retro − sinistres − 15/6/1/5 %
        // − pénalité). Le SOLDE DE RÉTROCESSION y ajoute la différence de solde de cession (signée) :
        // si la compagnie a sous-payé sa cession (diff < 0) le Pool déduit d'autant ; si elle a sur-payé
        // (diff > 0) le Pool ajoute. Fonctionne sur l'APERÇU comme sur la vue VALIDÉE (les composantes de
        // `cession` sont sérialisées et re-peuplées à chaque GET).
        const caRetroNet = (c) => {
            const emissions = Math.round(c.totalEmissions || 0);
            const sinistres = Math.round(c.totalSinistres || 0);
            return emissions - sinistres - igRetro(c) - Math.round(emissions * 0.06)
                - Math.round(emissions * 0.01) - Math.round(emissions * 0.05) - Math.round(c.penalite || 0);
        };
        const soldeRetroCession = (c) => caRetroNet(c) + diffSolde(c);

        const dataSource = [
            // ======== Section CESSION ========
            {
                key: 'cession-header',
                description: 'CESSION',
                isHeader: true,
                isCessionSection: true
            },
            {
                key: 'cession-souscriptions',
                description: 'MONTANT TOTAL DES SOUSCRIPTIONS',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(cessTotalEm(c));
                    return acc;
                }, {}),
                total: formatNumberForExport(totalCessionEmissions),
                isCessionSection: true
            },
            // Prime reportée (non répartie) + souscriptions nettes = cédée − reportée. Affichées
            // uniquement s'il existe une prime reportée (effet d'exercice futur cédé ce mois).
            ...(afficherReporte ? [
                {
                    key: 'cession-reportee',
                    description: 'EMISSION NETTE REPORTEE (non répartie)',
                    ...compagniesArray?.reduce((acc, c, index) => {
                        acc[`compagnie${index}`] = formatNumberForExport(-reporteParComp(c));
                        return acc;
                    }, {}),
                    total: formatNumberForExport(-totalReporte),
                    isCessionSection: true
                },
                {
                    key: 'cession-souscriptions-nettes',
                    description: 'TOTAL DES SOUSCRIPTIONS A RETROCEDER',
                    ...compagniesArray?.reduce((acc, c, index) => {
                        acc[`compagnie${index}`] = formatNumberForExport(souscriptionsNettesParComp(c));
                        return acc;
                    }, {}),
                    total: formatNumberForExport(totalSouscriptionsNettes),
                    isSolde: true,
                    isCessionSection: true
                },
            ] : []),
            {
                key: 'cession-sinistres',
                description: 'SINISTRES PAYES',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(-(c.cession?.totalSinistres || 0));
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalCessionSinistres),
                isCessionSection: true
            },
            {
                key: 'cession-indemnites',
                description: 'INDEMNITES DE GESTION 15%',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(-cessIndemnites(c));
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalCessionIndemnites),
                isCessionSection: true
            },
            {
                key: 'cession-solde',
                // Négatif ⇒ EN FAVEUR DE LA COMPAGNIE (le Pool est débiteur) ; positif ⇒ faveur Pool.
                description: 'Solde de Cession ---->',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(cessSoldeAffiche(c));
                    return acc;
                }, {}),
                total: formatNumberForExport(soldeCessionAffiche),
                isSolde: true,
                isCessionSection: true
            },
            // ======== Section RETROCESSION ========
            {
                key: 'retro-header',
                description: 'RETROCESSION',
                isHeader: true,
                isRetroSection: true
            },
            {
                key: 'retro-emission',
                description: 'MONTANT EMISSION NETTE RETRO',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(c.totalEmissions || 0);
                    return acc;
                }, {}),
                total: formatNumberForExport(totalRetroEmissions),
                isRetroSection: true
            },
            {
                key: 'retro-sinistres',
                description: 'SINISTRES REGLES RETRO',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(-(c.totalSinistres || 0));
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalRetroSinistres),
                isRetroSection: true
            },
            {
                key: 'retro-indemnite',
                description: 'INDEMNITE GESTION SUR CA RETRO 15%',
                ...compagniesArray?.reduce((acc, c, index) => {
                    const indemnite = igRetro(c);
                    acc[`compagnie${index}`] = formatNumberForExport(-indemnite);
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalRetroIndemnitesGestion),
                isRetroSection: true
            },
            {
                key: 'retro-frais',
                description: 'FRAIS ADMINISTRATIFS DU POOL 6%',
                ...compagniesArray?.reduce((acc, c, index) => {
                    const frais = Math.round((c.totalEmissions || 0) * 0.06);
                    acc[`compagnie${index}`] = formatNumberForExport(-frais);
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalRetroFraisAdmin),
                isRetroSection: true
            },
            {
                key: 'retro-prevention',
                description: 'PREVENTION ROUTIERE TOGOLAISE 1%',
                ...compagniesArray?.reduce((acc, c, index) => {
                    const prevention = Math.round((c.totalEmissions || 0) * 0.01);
                    acc[`compagnie${index}`] = formatNumberForExport(-prevention);
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalRetroPreventionRoutiere),
                isRetroSection: true
            },
            {
                key: 'retro-reserve',
                description: 'RESERVE DE GARANTIE 5%',
                ...compagniesArray?.reduce((acc, c, index) => {
                    const reserve = Math.round((c.totalEmissions || 0) * 0.05);
                    acc[`compagnie${index}`] = formatNumberForExport(-reserve);
                    return acc;
                }, {}),
                total: formatNumberForExport(-totalRetroReserveGarantie),
                isRetroSection: true
            },
            penalitesCession > 0 ? {
                key: 'penalites',
                description: 'PENALITES',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(-(c.penalite || 0));
                    return acc;
                }, {}),
                total: formatNumberForExport(-penalitesCession),
                isCessionSection: true
            } : null,

            // DIFFÉRENCE DE SOLDE DE CESSION (SIGNÉE) = montant chèque − solde de cession (diffSolde).
            //   UNE seule ligne, affichée AVEC son signe (le lecteur lit le sens via le signe) :
            //     • > 0 → en faveur de la COMPAGNIE ;
            //     • < 0 → en faveur du POOL.
            {
                key: 'retro-diff',
                description: 'DIFF. SOLDE CESSION',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(diffSolde(c));
                    return acc;
                }, {}),
                total: formatNumberForExport(
                    compagniesArray?.reduce((sum, c) => sum + diffSolde(c), 0) || 0
                ),
                isRetroSection: true
            },
            // SOLDE DE RETROCESSION = CA net retro + différence de solde de cession (signée).
            {
                key: 'retro-solde',
                description: 'Solde de Retrocession ---->',
                ...compagniesArray?.reduce((acc, c, index) => {
                    acc[`compagnie${index}`] = formatNumberForExport(soldeRetroCession(c));
                    return acc;
                }, {}),
                total: formatNumberForExport(
                    compagniesArray?.reduce((sum, c) => sum + soldeRetroCession(c), 0) || 0
                ),
                isSolde: true,
                isRetroSection: true
            }
        ].filter(Boolean);

        return { dataSource, compagniesArray };
    };

    // ============================================================
    // Source de données « RETRO PAR EXERCICE » (4 sections)
    //   • SOUSCRIPTIONS / SINISTRES, versant CÉDÉ (colonne = cédante) et RÉTRO (colonne = répartition)
    //   • ventilation par EXERCICE : émissions → année d'effet ; sinistres → année de survenance.
    // Alimenté par les LIGNES brutes (group.emissionsLignes / group.sinistresLignes), passées depuis
    // la mémoire front (le /preview ne renvoie que des totaux agrégés).
    // ============================================================
    // Lignes brutes du « Par exercice » : celles de l'aperçu (group.emissionsLignes / sinistresLignes) ;
    // sur une rétrocession VALIDÉE (pas de lignes en mémoire), le JSON stocké à la validation
    // (emissionsData / sinistresData) — IDENTIQUE sur toutes les compagnies du mois × branche → on lit
    // le premier lisible. Mêmes lignes qu'en pré-validation ⇒ même état par exercice.
    const lignesParExercice = (group, champLignes, champJson) => {
        if (Array.isArray(group?.[champLignes]) && group[champLignes].length > 0) return group[champLignes];
        for (const r of Object.values(group?.retrocessions || {})) {
            const json = r?.[champJson];
            if (!json) continue;
            try {
                const lignes = typeof json === 'string' ? JSON.parse(json) : json;
                if (Array.isArray(lignes) && lignes.length > 0) return lignes;
            } catch (_) {
                // JSON illisible → compagnie suivante
            }
        }
        return [];
    };

    const createDataSourceParExercice = (group) => {
        const compagniesArray = group?.retrocessions
            && Object.values(group?.retrocessions)
                ?.sort((a, b) => a.compagnie.code.localeCompare(b.compagnie.code, undefined, { numeric: true }));

        const emissions = lignesParExercice(group, 'emissionsLignes', 'emissionsData');
        const sinistres = lignesParExercice(group, 'sinistresLignes', 'sinistresData');
        const nz = (v) => Number(v) || 0;
        const codeOf = (c) => c?.compagnie?.code;

        // Ventile une liste de lignes par exercice.
        //   mode 'cede'  → colonne = la CÉDANTE (line.compagnie.code), montant = montantOriginal.
        //   mode 'retro' → colonne = chaque bénéficiaire (repartition[code].montant).
        const ventiler = (lignes, dateField, mode) => {
            const byYear = {};
            (lignes || []).forEach((l) => {
                const d = dayjs(l?.[dateField]);
                if (!l?.[dateField] || !d.isValid()) return;
                const y = d.year();
                if (!byYear[y]) byYear[y] = { perComp: {}, total: 0 };
                if (mode === 'cede') {
                    const idx = (compagniesArray || []).findIndex((c) => codeOf(c) === l?.compagnie?.code);
                    const m = nz(l.montantOriginal);
                    if (idx >= 0) byYear[y].perComp[idx] = (byYear[y].perComp[idx] || 0) + m;
                    byYear[y].total += m;
                } else {
                    (compagniesArray || []).forEach((c, idx) => {
                        const rep = (l.repartition || []).find((r) => r.compagnieCode === codeOf(c));
                        const m = nz(rep?.montant);
                        byYear[y].perComp[idx] = (byYear[y].perComp[idx] || 0) + m;
                        byYear[y].total += m;
                    });
                }
            });
            return byYear;
        };

        // Construit les lignes d'une section (une ligne par exercice + une ligne total).
        const sectionRows = (prefix, titre, byYear, totalLabel) => {
            const years = Object.keys(byYear).sort((a, b) => Number(a) - Number(b));
            const rows = [{ key: `${prefix}-header`, description: titre, isHeader: true }];
            const totaux = {};
            let grandTotal = 0;
            years.forEach((y) => {
                rows.push({
                    key: `${prefix}-${y}`,
                    description: `${y}`,
                    ...(compagniesArray || []).reduce((acc, c, idx) => {
                        const v = byYear[y].perComp[idx] || 0;
                        totaux[idx] = (totaux[idx] || 0) + v;
                        acc[`compagnie${idx}`] = v ? formatNumberForExport(v) : '';
                        return acc;
                    }, {}),
                    total: formatNumberForExport(byYear[y].total),
                });
                grandTotal += byYear[y].total;
            });
            rows.push({
                key: `${prefix}-total`,
                description: totalLabel,
                ...(compagniesArray || []).reduce((acc, c, idx) => {
                    acc[`compagnie${idx}`] = formatNumberForExport(totaux[idx] || 0);
                    return acc;
                }, {}),
                total: formatNumberForExport(grandTotal),
                isSolde: true,
            });
            return rows;
        };

        const dataSource = [
            ...sectionRows('souscr-cede', 'SOUSCRIPTIONS CEDEES',
                ventiler(emissions, 'effet', 'cede'), 'Total Souscriptions ---->'),
            ...sectionRows('sin-cede', 'SINISTRES PAYES CEDES',
                ventiler(sinistres, 'dateSinistre', 'cede'), 'Total Sinistres Payés ---->'),
            ...sectionRows('souscr-retro', 'SOUSCRIPTIONS RETRO',
                ventiler(emissions, 'effet', 'retro'), 'Total Souscriptions Rétro ---->'),
            ...sectionRows('sin-retro', 'SINISTRES PAYES RETRO',
                ventiler(sinistres, 'dateSinistre', 'retro'), 'Total Sinistres Payés Rétro ---->'),
        ];

        return { dataSource, compagniesArray };
    };

    // Y a-t-il des sinistres rétrocédés ? (conditionne l'affichage de l'onglet « Par exercice »).
    const aDesSinistres = (group) => lignesParExercice(group, 'sinistresLignes', 'sinistresData').length > 0;

    // ============================================================
    // Colonnes du tableau (réutilisable)
    // ============================================================
    const getColumns = (compagniesArray) => [
        {
            title: 'DESCRIPTION',
            dataIndex: 'description',
            key: 'description',
            fixed: 'left',
            width: 300,
            render: (text, record) => (
                <span style={{
                    fontWeight: record.isHeader || record.isSolde ? 'bold' : 'normal',
                    fontSize: record.isHeader ? '14px' : '12px',
                    color: record.isHeader ? '#722ed1' : 'inherit'
                }}>
                    {text}
                </span>
            )
        },
        ...(compagniesArray ?? []).map((c, index) => ({
            title: c.compagnie.raisonSociale.toUpperCase(),
            dataIndex: `compagnie${index}`,
            key: `compagnie${index}`,
            align: 'right',
            width: 150,
            render: (text, record) => (
                <span style={{
                    fontWeight: record.isSolde ? 'bold' : 'normal',
                    textDecoration: record.isSolde ? 'underline' : 'none'
                }}>
                    {text || ''}
                </span>
            )
        })),
        {
            title: 'TOTAL',
            dataIndex: 'total',
            key: 'total',
            align: 'right',
            width: 150,
            fixed: 'right',
            render: (text, record) => (
                <span style={{
                    // Colonne TOTAL toujours en gras (les « totaux »), soldes soulignés.
                    fontWeight: 'bold',
                    textDecoration: record.isSolde ? 'underline' : 'none',
                    fontSize: record.isHeader ? '14px' : '12px'
                }}>
                    {text || ''}
                </span>
            )
        }
    ];

    // ============================================================
    // Export Excel
    // ============================================================
    // Cœur d'export Excel générique (partagé « tous exercices » + « par exercice »).
    const doExportExcel = (dataSource, compagniesArray, titre, fileName) => {
        const excelData = [];

        const dateEdition = `Édité le ${dayjs().format('DD MMMM YYYY')} par ${LOGGED_USER.personne?.nom} ${LOGGED_USER.personne?.prenom}`;

        excelData.push([titre]);
        excelData.push([dateEdition]);
        excelData.push([]);

        const headers = ['DESCRIPTION'];
        compagniesArray?.forEach(c => {
            headers.push(c.compagnie.raisonSociale.toUpperCase());
        });
        headers.push('TOTAL');
        excelData.push(headers);

        dataSource.forEach(row => {
            if (row) {
                const rowData = [row.description];
                compagniesArray?.forEach((c, index) => {
                    rowData.push(row[`compagnie${index}`] || '');
                });
                rowData.push(row.total || '');
                excelData.push(rowData);
            }
        });

        const ws = XLSX.utils.aoa_to_sheet(excelData);
        ws['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } }
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Récapitulatif');

        XLSX.writeFile(wb, fileName);
    };

    const titreRapport = (prefixe, group) =>
        `${prefixe} ${group.branche?.codeBranche} ${frenchMonth(group?.moisRapport?.split('-')[1])} ${group?.moisRapport?.split('-')[0]}`;

    const exportToExcel = (group) => {
        const { dataSource, compagniesArray } = createDataSource(group);
        doExportExcel(dataSource, compagniesArray, titreRapport('RETRO TOUS EXERCICES', group),
            `RETRO_${group.branche?.codeBranche}_${group?.moisRapport}.xlsx`);
    };

    const exportToExcelParExercice = (group) => {
        const { dataSource, compagniesArray } = createDataSourceParExercice(group);
        doExportExcel(dataSource, compagniesArray, titreRapport('RETRO PAR EXERCICE', group),
            `RETRO_PAR_EXERCICE_${group.branche?.codeBranche}_${group?.moisRapport}.xlsx`);
    };

    // ============================================================
    // Export PDF (cœur générique partagé)
    // ============================================================
    const doExportPDF = async (dataSource, compagniesArray, titre, fileName) => {
        const doc = new jsPDF('landscape', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        let headerImage;
        try {
            headerImage = await loadImageAsBase64(entete);
        } catch (error) {
            console.error("Erreur chargement header:", error);
        }

        const insurances = await fetchInsuranceLogos();
        // Ré-encodage PNG (canvas) de chaque logo AVANT le rendu → jsPDF-safe (gère les PNG exotiques).
        const insuranceLogos = await Promise.all(
            insurances.map((ins) => sanitizeLogoForPdf(ins.logoDisplay))
        );

        const addHeader = () => {
            if (headerImage) {
                doc.addImage(headerImage, 'PNG', 0, 0, pageWidth, 30);
            }
        };

        const addFooter = (pageNum) => {
            const footerY = pageHeight - 35;

            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.5);
            doc.line(10, footerY, pageWidth - 10, footerY);

            doc.setFontSize(7);
            doc.setTextColor(30, 58, 138);
            const contactInfo = 'Tél: +228 22-20-55-77 / +228 22-20-55-78 | Whatsapp: +228 70-48-11-11 | Télécopie: +228 22-20-55-80 | N°RC: 2009 C 0771 - N°COE: 1000 16 1028';
            doc.text(contactInfo, pageWidth / 2, footerY + 5, { align: 'center' });

            if (insurances.length > 0) {
                const logoWidth = 15;
                const logoHeight = 10;
                const spacing = 40;
                const totalWidth = insurances.length * (logoWidth + spacing) - spacing;
                let startX = (pageWidth - totalWidth) / 2;

                insurances.forEach((insurance, index) => {
                    const logoData = insuranceLogos[index]; // PNG ré-encodé (null si indisponible)
                    if (logoData) {
                        try {
                            doc.addImage(logoData, 'PNG', startX, footerY + 8, logoWidth, logoHeight);
                        } catch (error) {
                            console.error(`Erreur ajout logo ${index}:`, error);
                        }
                    }
                    // Avance TOUJOURS d'un emplacement → espacement régulier même si un logo manque.
                    startX += logoWidth + spacing;
                });
            }

            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.text('Entreprises régies par le code des assurances', pageWidth / 2, footerY + 20, { align: 'center' });

            doc.setFontSize(8);
            doc.setFont(undefined, 'normal');
            doc.text(`Page ${pageNum}`, pageWidth - 15, footerY + 25, { align: 'right' });
        };

        addHeader();

        const dateEdition = `Édité le ${dayjs().format('DD MMMM YYYY')} par ${LOGGED_USER?.personne?.nom} ${LOGGED_USER?.personne?.prenom}`;

        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        doc.text(titre, pageWidth / 2, 40, { align: 'center' });

        doc.setFontSize(10);
        doc.setTextColor(102, 102, 102);
        doc.text(dateEdition, pageWidth / 2, 47, { align: 'center' });

        const headers = ['DESCRIPTION'];
        compagniesArray?.forEach(c => {
            headers.push(c.compagnie.raisonSociale.toUpperCase());
        });
        headers.push('TOTAL');

        const body = [];
        dataSource.forEach(row => {
            if (row) {
                const rowData = [row.description];
                compagniesArray?.forEach((c, index) => {
                    rowData.push(row[`compagnie${index}`] || '');
                });
                rowData.push(row.total || '');
                body.push(rowData);
            }
        });

        autoTable(doc, {
            head: [headers],
            body: body,
            startY: 52,
            margin: { top: 35, bottom: 40, left: 10, right: 10 },
            styles: {
                fontSize: 7,
                cellPadding: 1.5,
                lineColor: [200, 200, 200],
                lineWidth: 0.1
            },
            headStyles: {
                fillColor: [36, 33, 102],
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                halign: 'center'
            },
            columnStyles: {
                0: { cellWidth: 60, fontStyle: 'normal' }
            },
            didParseCell: function (data) {
                if (data.section === 'body') {
                    const row = dataSource[data.row.index];
                    if (row) {
                        if (row.isHeader) {
                            data.cell.styles.fontStyle = 'bold';
                            data.cell.styles.fillColor = [240, 240, 240];
                            data.cell.styles.textColor = [36, 33, 102];
                        }
                        if (row.isSolde) {
                            data.cell.styles.halign = 'right';
                            data.cell.styles.fontStyle = 'bold';
                            data.cell.styles.fontSize = 8;
                        }
                        if (data.column.index > 0) {
                            data.cell.styles.halign = 'right';
                        }
                        // Colonne TOTAL (dernière) toujours en gras (les « totaux »).
                        if (data.column.index === data.table.columns.length - 1) {
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                }
            },
            didDrawPage: function (data) {
                addFooter(doc.internal.getCurrentPageInfo().pageNumber);
                if (doc.internal.getCurrentPageInfo().pageNumber > 1) {
                    addHeader();
                }
            }
        });

        doc.save(fileName);
    };

    const exportToPDF = async (group) => {
        const { dataSource, compagniesArray } = createDataSource(group);
        await doExportPDF(dataSource, compagniesArray, titreRapport('RETRO TOUS EXERCICES', group),
            `RETRO_${group.branche?.codeBranche}_${group?.moisRapport}.pdf`);
    };

    const exportToPDFParExercice = async (group) => {
        const { dataSource, compagniesArray } = createDataSourceParExercice(group);
        await doExportPDF(dataSource, compagniesArray, titreRapport('RETRO PAR EXERCICE', group),
            `RETRO_PAR_EXERCICE_${group.branche?.codeBranche}_${group?.moisRapport}.pdf`);
    };

    // ============================================================
    // Rendu d'un tableau récap (générique) à partir d'une source déjà calculée.
    // ============================================================
    const renderTableau = (dataSource, compagniesArray) => {
        const columns = getColumns(compagniesArray);

        return (
            <Table
                dataSource={dataSource}
                columns={columns}
                pagination={false}
                bordered
                size="small"
                scroll={{ x: 1500 }}
                rowClassName={(record) => {
                    if (record.isHeader) return 'table-header-row';
                    if (record.isSolde) return 'table-solde-row';
                    return record.isCessionSection ? 'table-cession-row' : 'table-retro-row';
                }}
            />
        );
    };

    // En-tête d'un onglet (titre + date d'édition + boutons d'export).
    const renderPanel = (titrePrefix, dataSource, compagniesArray, onExcel, onPdf) => (
        <div>
            <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <h3>
                    {titrePrefix} {selectedGroup.branche?.codeBranche}{' '}
                    {frenchMonth(selectedGroup?.mois?.split('-')[1] || selectedGroup?.moisRapport?.split('-')[1])}{' '}
                    {selectedGroup?.mois?.split('-')[0] || selectedGroup?.moisRapport?.split('-')[0]}
                </h3>
                <p style={{ fontSize: '12px', color: '#666' }}>
                    Édité le {dayjs().format('DD MMMM YYYY')} par {LOGGED_USER.nom}{' '}
                    {LOGGED_USER.prenom}
                </p>

                <div style={{ marginTop: '10px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <button
                        onClick={onExcel}
                        style={{ padding: '8px 16px', cursor: 'pointer', border: 'none', backgroundColor: '#21b317', color: '#fff', borderRadius: '4px' }}
                    >
                        📊 Exporter en Excel
                    </button>
                    <button
                        onClick={onPdf}
                        style={{ padding: '8px 16px', cursor: 'pointer', border: 'none', backgroundColor: '#722ed1', color: '#fff', borderRadius: '4px' }}
                    >
                        📄 Exporter en PDF
                    </button>
                </div>
            </div>
            {renderTableau(dataSource, compagniesArray)}
        </div>
    );

    const tousExercices = createDataSource(selectedGroup);
    const panelTousExercices = renderPanel(
        'RETRO TOUS EXERCICES', tousExercices.dataSource, tousExercices.compagniesArray,
        () => exportToExcel(selectedGroup), () => exportToPDF(selectedGroup));

    // Sans sinistres rétrocédés → uniquement le récap « tous exercices » (comportement historique).
    if (!aDesSinistres(selectedGroup)) {
        return panelTousExercices;
    }

    // Avec sinistres → 1er onglet « Par exercice » (survenance), 2ᵉ onglet « Tous exercices ».
    const parExercice = createDataSourceParExercice(selectedGroup);
    return (
        <Tabs
            defaultActiveKey="parExercice"
            items={[
                {
                    key: 'parExercice',
                    label: '📅 Par exercice',
                    children: renderPanel(
                        'RETRO PAR EXERCICE', parExercice.dataSource, parExercice.compagniesArray,
                        () => exportToExcelParExercice(selectedGroup), () => exportToPDFParExercice(selectedGroup)),
                },
                {
                    key: 'tousExercices',
                    label: '📊 Tous exercices',
                    children: panelTousExercices,
                },
            ]}
        />
    );
}

export default TableauRecapitulatifRetrocession